import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as moment from 'moment';
import axios from 'axios';
import SessionProp, { PARTICIPATION_STAT } from '../propTypes/sessionProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import {
  SESSION_MODE,
  SESSION_STATUS,
} from '../../../services/utilityFunctions';
import Notifications from '../../../services/notifications';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import PriceDisplay from '../../../commonComponents/PriceDisplay';
import UserBannerCard from '../../../commonComponents/users/UserBannerCard';
import AcademyFooterContainer from '../../../commonComponents/container/AcademyFooterContainer';
import MarkerIcon from '../../../commonComponents/icons/MarkerIcon';
import LanguagesIcon from '../../../commonComponents/icons/LanguagesIcon';
import { useModal } from '../../../services/customHooks';
import CancellationPolicyModal from '../../../commonComponents/modal/CancellationPolicyModal';

export default function SessionItemDetail({
  t,
  sessions,
  onEnroll,
  languages,
  userId,
  _csrf,
  isFromJourney,
  journeyIsFree,
  onRefresh,
}) {
  const {
    price,
    purchaseStatus,
    owner,
    trainer,
    host,
    userPrice,
    userPromotionsPrice,
    mode,
    participationStatus,
    image,
    participants,
    name,
    summary,
    language,
    type,
    startDate: sessionStartDate,
    endDate: sessionEndDate,
    isContentAccessible,
  } = sessions;
  // if session is private and user already in participants we shouldn't show enroll option
  const hideButton =
    mode === 'private' && !!participants.find(u => u === userId);
  const { loggedUser } = useContext(LoggedUserContext);

  const [isLoading, setIsLoading] = useState(false);
  const [cancellationModal, setCancellationModal] = useModal({
    show: false,
    data: null,
  });
  const isPublic = mode === SESSION_MODE.PUBLIC;
  const shouldSubscribe =
    (isPublic && participationStatus === PARTICIPATION_STAT.NOT_SUBSCRIBED) ||
    (!isPublic && participationStatus === PARTICIPATION_STAT.INVITED);

  const isFree = !price || price === 0;

  // A session is treated as "covered" (free to subscribe) when the user paid
  // for it directly, or when it lives inside a PAID journey (one payment covers
  // its sessions). In a free journey a paid session must be paid as normal, so
  // it is not auto-covered. Standalone page: isFromJourney is null -> unchanged.
  const isPaid =
    purchaseStatus === 'paid' || (!!isFromJourney && !journeyIsFree);

  /* owner and trainer do not need to subscribe or enroll */
  const doesNotNeedSubscription =
    userId === owner || userId === trainer?._id || userId === host?._id;

  // Start and End Date
  let duration = null;
  if (sessionStartDate && sessionEndDate) {
    const startDate = moment(sessionStartDate).format('DD MMM, YYYY HH:mm');
    const endDate = moment(sessionEndDate).format('DD MMM, YYYY HH:mm');
    duration = `${startDate} - ${endDate}`;
  }
  // Footer - Language
  const languageValue = useMemo(() => {
    let languageStr = null;
    if (languages && languages.length > 0) {
      const lang = languages.find(l => l.code === language) || {};
      languageStr = lang.native || t('all');
    }
    return languageStr;
  }, [language, languages]);

  const handleSubscribe = () => {
    setIsLoading(true);
    axios
      .put(
        `/api/access-session/${sessions._id}/subscribe`,
        {},
        {
          headers: {
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('success_subscribe_access_session'),
        );
        if (!isFromJourney) {
          window.location.href = `/academy/sessions/${sessions.slug}`;
        } else {
          onRefresh();
          setIsLoading(false);
        }
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err);
        console.log('Err', err);
        setIsLoading(false);
      });
  };

  const handleCancellationPolicy = useCallback(
    open => {
      setCancellationModal({ show: open, data: null });
    },
    [setCancellationModal],
  );

  const handleCancelSubscription = cancelSubscription => {
    if (cancelSubscription) {
      setIsLoading(true);
      axios
        .put(
          `/api/access-session/${sessions._id}/cancel`,
          {},
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(() => {
          Notifications.showNotificationSuccess(
            t('success'),
            t('success_cancel_access_session'),
          );
          handleCancellationPolicy(false);
          if (!isFromJourney) {
            window.location.href = `/academy/sessions/${sessions.slug}`;
          } else {
            onRefresh();
            setIsLoading(false);
          }
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err);
          console.log('Err', err);
          setIsLoading(false);
        });
    } else {
      handleCancellationPolicy(false);
    }
  };

  const containerClass = cx(
    'm-portlet',
    'm-portlet--rounded',
    'm-portlet--rounded-force',
    'academy-session-item',
  );

  const imageURL = image ? image.thumb : '/images/no_image_upload.png';

  const subscribeButtonClasses = cx(
    'btn    ',
    'btn-sm',
    'm-btn--pill',
    'btn-dynamic',
    'navigation-button',
    'col-12',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
    { 'm-loader--light': isLoading },
  );

  const isCancelSubscriptionAvailable = participants?.find(
    u => u === loggedUser?._id,
  );

  return (
    <>
      {cancellationModal.show && (
        <CancellationPolicyModal onClose={handleCancelSubscription} />
      )}
      <div className={containerClass}>
        <div className="m-portlet__body session-container-body session-container-content">
          <div className="row">
            {/** IMAGE */}
            <div className="col-lg-2 col-md-3">
              <img className="session-image" src={imageURL} alt={name} />
            </div>
            <div className="col-lg-10 col-12">
              <div className="row">
                {/** NAME & SUMMARY */}
                <div className="col-lg-9 col-md-6">
                  <div className="session-details">
                    <div className="session-header">
                      <span className="session-header-name">{name}</span>
                    </div>
                    <div className="session-description-and-progress">
                      <div className="session-description">{summary}</div>
                    </div>
                  </div>
                </div>
                {/** PRICE */}
                <div
                  className="col-lg-3 col-12 px-0"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <PriceDisplay
                    type="session"
                    t={t}
                    userPrice={userPrice}
                    userPromotionPrice={userPromotionsPrice}
                    withWrapper
                  />
                </div>
                <div className="col-lg-12 col-12 host-trainer-container">
                  {host && (
                    <UserBannerCard user={host} label={t('sessions_host')} />
                  )}
                  {trainer && (
                    <UserBannerCard
                      user={trainer}
                      label={t('sessions_trainer')}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="m-portlet__foot session-container-footer row">
          <div className="footer-info-container">
            {type && (
              <AcademyFooterContainer
                customIcon={<MarkerIcon />}
                text={t(`sessions_${type}`)}
              />
            )}
            {duration?.length > 0 && (
              <AcademyFooterContainer
                faIcon="la la-clock-o"
                text={duration || ''}
              />
            )}
            {languageValue && languageValue?.length > 0 && (
              <AcademyFooterContainer
                customIcon={<LanguagesIcon />}
                text={languageValue || ''}
              />
            )}
          </div>
          {sessions.userStatus === SESSION_STATUS.UNSTARTED && (
            <div className="session-actions row">
              {(isFree || isPaid) &&
                shouldSubscribe &&
                !doesNotNeedSubscription && (
                  <button
                    type="button"
                    onClick={handleSubscribe}
                    className={subscribeButtonClasses}
                    disabled={isLoading}
                  >
                    {t('sessions_subscribe')}
                  </button>
                )}
              {onEnroll &&
                (!isContentAccessible ||
                  (!isFree && !doesNotNeedSubscription)) &&
                !hideButton && (
                  <button
                    type="button"
                    onClick={onEnroll}
                    className="btn btn-sm m-btn--pill btn-dynamic navigation-button col-12"
                  >
                    {t('sessions_enroll')}
                  </button>
                )}
              {(isFree || isPaid) &&
                isCancelSubscriptionAvailable &&
                !doesNotNeedSubscription && (
                  <button
                    type="button"
                    className={`btn btn-sm m-btn--pill btn-danger navigation-button cancel-subscription col-12 ${
                      isLoading
                        ? 'm-loader m-loader--right m-loader--light'
                        : ''
                    }`}
                    disabled={isLoading}
                    onClick={() => handleCancellationPolicy(true)}
                  >
                    {t('session.table.participation.cancel.subscription')}
                  </button>
                )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

SessionItemDetail.propTypes = {
  t: PropTypes.func.isRequired,
  sessions: PropTypes.shape(SessionProp.propType).isRequired,
  // undefined in journey context — the enroll button is not rendered there
  onEnroll: PropTypes.func,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  userId: PropTypes.string,
  _csrf: PropTypes.string.isRequired,
  isFromJourney: PropTypes.string,
  journeyIsFree: PropTypes.bool,
  onRefresh: PropTypes.func,
};

SessionItemDetail.defaultProps = {
  userId: '',
  isFromJourney: null,
  journeyIsFree: false,
  onRefresh: undefined,
  onEnroll: undefined,
};
